import { formatDateTime } from '@/lib/date-time';
/**
 * @file components/settings/DocumentLibraryPage.tsx
 * @description 文件資料庫（序180/181）。
 *
 * 系統設置底下的文件資料庫彙整頁：
 *   - 頂部兩張連結卡片導向既有獨立模組（表單模板管理／資材庫管理），不重複建模
 *   - 「管理手冊」「公文」「法規/標準」「稽核報告彙整」四個 Tab，各自是一個通用文件庫
 *     （複用既有 files 表 + OSS 直傳上傳，`scope='document-library'`）
 *   - 公文額外帶 TOP/TAP 分類；稽核報告彙整支援批量上傳與勾選打包下載
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  Popconfirm,
  Progress,
  Segmented,
  Space,
  Table,
  Tabs,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  BookOpen,
  Download,
  FileStack,
  FlaskConical,
  Mail,
  Package,
  ScrollText,
  Trash2,
} from 'lucide-react';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { uploadFile, uploadFiles } from '@/lib/file-upload';
import { downloadFile } from '@/lib/file-preview';
import type {
  DocumentLibraryCategory,
  DocumentLibraryItemDto,
  DocumentLibraryScheme,
} from '@zhcb/shared';

/** 後端分頁回應結構 */
interface DocumentLibraryListResponse {
  items: DocumentLibraryItemDto[];
  total: number;
}

/** 位元組數轉人類可讀容量 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 連結卡片：導向既有獨立模組，不在文件資料庫內重複建模 */
function LinkoutCard({
  icon,
  title,
  desc,
  to,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  to: string;
}) {
  return (
    <Card size="small" style={{ flex: 1, minWidth: 220 }}>
      <Space align="start" size={10}>
        <div style={{ color: 'var(--ant-color-primary)', marginTop: 2 }}>{icon}</div>
        <div>
          <Typography.Text strong>{title}</Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
              {desc}
            </Typography.Text>
          </div>
          <Link to={to}>
            <Button type="link" size="small" style={{ paddingLeft: 0 }}>
              前往管理 →
            </Button>
          </Link>
        </div>
      </Space>
    </Card>
  );
}

/** 單一分類的文件庫（管理手冊 / 公文 / 法規標準 / 稽核報告彙整共用同一套 UI） */
function CategoryLibrary({
  category,
  allowScheme,
  allowBatch,
}: {
  category: DocumentLibraryCategory;
  /** 公文分類需要 TOP/TAP 篩選與上傳時分類 */
  allowScheme?: boolean;
  /** 稽核報告彙整需要批量上傳 + 勾選打包下載 */
  allowBatch?: boolean;
}) {
  const qc = useQueryClient();
  const queryKey = ['document-library', category];
  const [schemeFilter, setSchemeFilter] = useState<DocumentLibraryScheme | 'all'>('all');
  const [uploadPct, setUploadPct] = useState<Record<string, number>>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKey, schemeFilter],
    queryFn: () => {
      const p = new URLSearchParams({ category, pageSize: '200' });
      if (allowScheme && schemeFilter !== 'all') p.set('scheme', schemeFilter);
      return apiClient.get<DocumentLibraryListResponse>(
        `/api/settings/document-library?${p.toString()}`,
      );
    },
  });
  const items = data?.items ?? [];

  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/settings/document-library/${id}`),
    onSuccess: () => {
      message.success('已刪除');
      void qc.invalidateQueries({ queryKey });
    },
    onError: (err) => message.error(err instanceof Error ? err.message : '刪除失敗'),
  });

  /** 單檔上傳（管理手冊/法規標準/公文），公文帶目前篩選的 scheme（若為「全部」則不帶，需另外選） */
  async function handleUploadOne(file: RcFile) {
    const key = file.name;
    setUploadPct((p) => ({ ...p, [key]: 0 }));
    try {
      await uploadFile(file, {
        scope: 'document-library',
        ownerType: category,
        onProgress: (pct) => setUploadPct((p) => ({ ...p, [key]: pct })),
        meta: allowScheme ? { scheme: schemeFilter === 'all' ? 'TOP' : schemeFilter } : undefined,
      });
      message.success(`${file.name} 已上傳`);
      void qc.invalidateQueries({ queryKey });
    } catch (err) {
      message.error(err instanceof Error ? err.message : `${file.name} 上傳失敗`);
    } finally {
      setUploadPct((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
    return false;
  }

  /** 批量上傳（稽核報告彙整） */
  async function handleUploadBatch(fileList: File[]) {
    const { succeeded, failed } = await uploadFiles(fileList, {
      scope: 'document-library',
      ownerType: category,
      onProgress: (done, total) => setUploadPct({ 批量上傳: (done / total) * 100 }),
    });
    setUploadPct({});
    if (succeeded.length) message.success(`已上傳 ${succeeded.length} 個檔案`);
    if (failed.length) message.error(`${failed.length} 個檔案上傳失敗`);
    void qc.invalidateQueries({ queryKey });
  }

  function handleExportZip() {
    if (!selectedIds.length) {
      message.warning('請先勾選要打包下載的文件');
      return;
    }
    window.location.href = `/api/settings/document-library/export.zip?ids=${selectedIds.join(',')}`;
  }

  const columns: ColumnsType<DocumentLibraryItemDto> = [
    {
      title: '檔案名稱',
      dataIndex: 'filename',
      ellipsis: true,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{v}</span>,
    },
    ...(allowScheme
      ? [
          {
            title: '方案',
            dataIndex: 'scheme',
            width: 80,
            render: (v: string | null) => v ?? '—',
          } as ColumnsType<DocumentLibraryItemDto>[number],
        ]
      : []),
    { title: '大小', dataIndex: 'sizeBytes', width: 90, render: formatBytes },
    {
      title: '上傳時間',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: '_actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Download size={13} />}
            onClick={() => void downloadFile(record.id)}
          />
          <Popconfirm
            title="確定刪除此文件？"
            onConfirm={() => remove.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const uploadingList = Object.entries(uploadPct);

  return (
    <Space direction="vertical" size={12} style={{ display: 'flex' }}>
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space wrap>
          {allowScheme && (
            <Segmented
              value={schemeFilter}
              onChange={(v) => setSchemeFilter(v as typeof schemeFilter)}
              options={[
                { label: '全部', value: 'all' },
                { label: 'TOP', value: 'TOP' },
                { label: 'TAP', value: 'TAP' },
              ]}
            />
          )}
        </Space>
        <Space>
          {allowBatch && selectedIds.length > 0 && (
            <Button icon={<Archive size={14} />} onClick={handleExportZip}>
              打包下載已選（{selectedIds.length}）
            </Button>
          )}
          <Upload
            showUploadList={false}
            multiple={allowBatch}
            beforeUpload={(file, fileList) => {
              if (allowBatch && fileList.length > 1) {
                void handleUploadBatch(fileList);
              } else {
                void handleUploadOne(file);
              }
              return false;
            }}
          >
            <Button type="primary" size="small">
              上傳文件
            </Button>
          </Upload>
        </Space>
      </Space>

      {uploadingList.length > 0 && (
        <div>
          {uploadingList.map(([name, pct]) => (
            <div key={name} style={{ marginBottom: 4 }}>
              <Typography.Text style={{ fontSize: 12 }}>{name}</Typography.Text>
              <Progress percent={Math.round(pct)} size="small" status="active" />
            </div>
          ))}
        </div>
      )}

      <Table<DocumentLibraryItemDto>
        rowKey="id"
        size="small"
        dataSource={items}
        columns={columns}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: '尚無文件' }}
        rowSelection={
          allowBatch
            ? {
                selectedRowKeys: selectedIds,
                onChange: (keys) => setSelectedIds(keys as number[]),
              }
            : undefined
        }
      />
    </Space>
  );
}

/** 文件資料庫主頁面 */
export function DocumentLibraryPage() {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        文件資料庫
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        彙整管理手冊、公文、法規/標準、稽核報告彙整；表單管理與資材庫為既有獨立模組，於此提供快速入口。
      </Typography.Paragraph>

      <Space wrap style={{ width: '100%' }}>
        <LinkoutCard
          icon={<FileStack size={18} />}
          title="表單管理"
          desc="ZHSR 系列表單、版本、校對與代碼維護"
          to="/settings/form-templates"
        />
        <LinkoutCard
          icon={<Package size={18} />}
          title="資材庫管理"
          desc="有機／產銷履歷可用資材清單（依 approvedSchemes 區分方案）"
          to="/settings/materials"
        />
      </Space>

      <Tabs
        items={[
          {
            key: 'manual',
            label: (
              <Space size={6}>
                <BookOpen size={14} /> 管理手冊
              </Space>
            ),
            children: <CategoryLibrary category="manual" />,
          },
          {
            key: 'official-notice',
            label: (
              <Space size={6}>
                <Mail size={14} /> 公文
              </Space>
            ),
            children: <CategoryLibrary category="official-notice" allowScheme />,
          },
          {
            key: 'regulation',
            label: (
              <Space size={6}>
                <ScrollText size={14} /> 法規/標準
              </Space>
            ),
            children: <CategoryLibrary category="regulation" />,
          },
          {
            key: 'audit-report-archive',
            label: (
              <Space size={6}>
                <FlaskConical size={14} /> 稽核報告彙整
              </Space>
            ),
            children: <CategoryLibrary category="audit-report-archive" allowBatch />,
          },
        ]}
      />
    </Space>
  );
}
